/**
 * Decides what the music is doing, the way `audio/director.ts` decides what the
 * sound effects are doing.
 *
 * The conductor owns one sequencer and points it at a track, a tempo and a set
 * of audible layers, all derived from state the game already publishes: the
 * screen, the mode, the level, the phase, and — in BALANCE — how far the
 * platform is leaning. It subscribes to the core's events for the two moments
 * that need a reaction rather than a reading: a line clear, which ducks the
 * music under the cue, and a game over, which tape-stops it.
 *
 * It holds no browser types. Every decision here is exercised in Node against a
 * recording `MusicBoard`.
 */

import { Phase, RuleSet, type GameState, type LineClearEvent } from '../core/game.ts';
import type { MusicBoard } from './board.ts';
import { Sequencer } from './sequencer.ts';
import { TRACKS, bpmForLevel, type LayerId, type TrackId, type TrackSpec } from './score.ts';

/** How far the music drops under an ordinary clear, and for how long. */
export const DUCK_CLEAR = 0.5;
export const DUCK_CLEAR_MS = 250;
/** ...and under a Tetris, a T-spin, or a topple. */
export const DUCK_BIG = 0.32;
export const DUCK_BIG_MS = 400;

/** The tape stop at the end of a run. */
export const GAME_OVER_FADE_MS = 1200;

/** The interface state the conductor reads. Structural, so `Shell` fits. */
export interface MusicSource {
  screen: string;
  gameMode: string;
}

export class Conductor {
  private readonly sequencer = new Sequencer(TRACKS.menu);
  private readonly offs: (() => void)[] = [];
  private enabledValue = true;
  private playing = false;
  private layers: ReadonlySet<LayerId> = new Set();
  private bpmValue = TRACKS.menu.baseBpm;

  constructor(private readonly board: MusicBoard) {}

  get enabled(): boolean {
    return this.enabledValue;
  }

  get trackId(): TrackId {
    return this.sequencer.track.id;
  }

  get bpm(): number {
    return this.bpmValue;
  }

  /** Visible so the layer curve can be asserted without listening to it. */
  get activeLayers(): ReadonlySet<LayerId> {
    return this.layers;
  }

  /** Subscribe to a game's events. Returns an unsubscribe function. */
  attach(game: GameState): () => void {
    const offs = [
      game.events.on('onLineClear', (event) => this.onLineClear(event)),
      game.events.on('onGameOver', () => {
        this.setPlaying(false);
        this.board.stop(GAME_OVER_FADE_MS);
        this.sequencer.reset();
      }),
    ];
    this.offs.push(...offs);
    return () => {
      for (const off of offs) off();
    };
  }

  setEnabled(enabled: boolean): void {
    this.enabledValue = enabled;
    if (!enabled) this.setPlaying(false);
  }

  /** Advance the score. Call once per frame, after the simulation steps. */
  step(dtMs: number, ui: MusicSource, game: GameState): void {
    const track = trackFor(ui);
    if (track.id !== this.sequencer.track.id) this.sequencer.setTrack(track);

    // The menu is a fixed tempo: it should not inherit the pace of whatever
    // run the player just finished.
    const inGame = ui.screen === 'GAME';
    this.bpmValue = inGame ? bpmForLevel(track.baseBpm, game.level) : track.baseBpm;
    this.sequencer.setBpm(this.bpmValue);

    this.layers = layersFor(track, inGame ? game.level : Number.MAX_SAFE_INTEGER);
    this.sequencer.setLayers(this.layers);

    if (track.id === 'balance' && game.rules === RuleSet.Balance) {
      this.sequencer.setLayerIntensity('tilt', Math.min(1, game.balanceState().risk));
    }

    const shouldPlay = this.enabledValue && (!inGame || game.phase === Phase.Playing);
    this.setPlaying(shouldPlay);
    if (!shouldPlay) return;

    for (const note of this.sequencer.step(dtMs)) this.board.note(note);
  }

  private setPlaying(playing: boolean): void {
    if (playing === this.playing) return;
    this.playing = playing;
    this.board.setPlaying(playing);
  }

  private onLineClear(event: LineClearEvent): void {
    const big = event.lines >= 4 || event.tspin !== 'none';
    this.board.duck(big ? DUCK_BIG : DUCK_CLEAR, big ? DUCK_BIG_MS : DUCK_CLEAR_MS);
  }
}

function trackFor(ui: MusicSource): TrackSpec {
  if (ui.screen !== 'GAME') return TRACKS.menu;
  if (ui.gameMode === 'BALANCE') return TRACKS.balance;
  if (ui.gameMode === 'TWIN') return TRACKS.twin;
  return TRACKS.solo;
}

/**
 * Every layer the level has unlocked. The menu passes an unbounded level so its
 * three layers all sound from the first bar — it has no progression to reveal.
 */
function layersFor(track: TrackSpec, level: number): ReadonlySet<LayerId> {
  const active = new Set<LayerId>();
  for (const layer of track.layers) {
    if (layer.enterLevel <= level) active.add(layer.id);
  }
  return active;
}
